// Web component entry point for widget build
// This file is used when building with BUILD_TARGET=widget

import React from 'react'
import ReactDOM from 'react-dom/client'
import { ChatWidget, type WidgetMode } from './ChatWidget'
import styles from './widget-styles.css?inline'
import cardStyles from '../../shared/cards/widget-card-styles.css?inline'

class SuperviaggiChatWidget extends HTMLElement {
  private root: ReactDOM.Root | null = null
  private shadow: ShadowRoot | null = null

  static get observedAttributes() {
    return ['api-url', 'mode']
  }

  private getMode(): WidgetMode {
    const mode = this.getAttribute('mode')
    return mode === 'popup' ? 'popup' : 'embedded'
  }

  connectedCallback() {
    this.shadow = this.attachShadow({ mode: 'open' })

    // Inject styles into Shadow DOM
    const styleSheet = document.createElement('style')
    styleSheet.textContent = `${styles}\n${cardStyles}`
    this.shadow.appendChild(styleSheet)

    // Create mount point
    const mountPoint = document.createElement('div')
    mountPoint.id = 'superviaggi-widget-root'
    const mode = this.getMode()
    // Embedded mode: container fills parent so the chat takes full space
    // Popup mode: no size constraints, the floating widget uses fixed positioning
    mountPoint.style.cssText = mode === 'popup' ? '' : 'width: 100%; height: 100%;'
    this.shadow.appendChild(mountPoint)

    // Get optional api-url attribute
    const apiUrl = this.getAttribute('api-url') || undefined

    this.root = ReactDOM.createRoot(mountPoint)
    this.root.render(
      <React.StrictMode>
        <ChatWidget apiUrl={apiUrl} mode={mode} />
      </React.StrictMode>
    )
  }

  disconnectedCallback() {
    this.root?.unmount()
    this.root = null
  }

  attributeChangedCallback(_name: string, oldValue: string, newValue: string) {
    if (oldValue !== newValue && this.root && this.shadow) {
      const mountPoint = this.shadow.querySelector('#superviaggi-widget-root')
      if (mountPoint) {
        const apiUrl = this.getAttribute('api-url') || undefined
        const mode = this.getMode()
        this.root.render(
          <React.StrictMode>
            <ChatWidget apiUrl={apiUrl} mode={mode} />
          </React.StrictMode>
        )
      }
    }
  }
}

// Only register if not already registered
if (!customElements.get('superviaggi-chat-widget')) {
  customElements.define('superviaggi-chat-widget', SuperviaggiChatWidget)
}
